import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'

/**
 * Drag-to-select a run of days (CAP-1403): press on a day, drag across others,
 * release. Shared by the property Calendar tab's month grid and the portfolio
 * calendar's rows (CAP-1419), so both read a press, a drag and a release the
 * same way; each says only which day is under the pointer and what a
 * selection opens.
 *
 * - The range is the pressed day to the day last reached, in order whichever
 *   way the pointer travelled. A press and release on one day is not a drag:
 *   nothing is selected and the caller's own click handling sees it.
 * - Mouse and pen only: a finger keeps scrolling the page.
 * - The listeners are on `window`, not a captured element, so a release over
 *   anything (a bar, the page chrome, outside the grid) still ends the drag. A
 *   move with the button no longer held, a cancelled pointer, the window
 *   losing focus and Escape all drop it.
 * - Only the range is state, to repaint the highlight, and it changes only
 *   when the day under the pointer does.
 *
 * Days are whatever key the caller gives (an ISO date, a day number), compared
 * with `<`, so they must sort in calendar order.
 */

export interface DragPoint {
  clientX: number
  clientY: number
  target: EventTarget | null
}

export interface DragRange<K> {
  first: K
  last: K
}

export interface DragSelectOptions<K extends string | number> {
  /** The day under a point, or null when it is over none. */
  dayAt: (point: DragPoint) => K | null
  /** A drag released: its first and last day, in calendar order. */
  onSelect: (first: K, last: K) => void
  /**
   * A drag in progress is dropped whenever this changes (another month, drags
   * switched off), and when the component goes away.
   */
  scope: string
  /**
   * How far (px) the pointer may travel from the press before it counts as
   * moving at all. Left at 0, any move does.
   */
  slop?: number
  /** Each pointer move of a drag past the slop, after the range is updated. */
  onMove?: (point: DragPoint) => void
  /** The press became a drag: the pointer reached a day other than the one pressed. */
  onDragged?: () => void
  /** The drag is over: released, cancelled or dropped. */
  onEnd?: () => void
}

export interface DragSelect<K> {
  /** The days to highlight, or null when no drag spans more than one day. */
  range: DragRange<K> | null
  /** Arms a drag from a press on `day`. Ignores all but the main button, and touch. */
  start: (event: ReactPointerEvent<Element>, day: K) => void
  /**
   * Reads the day under the pointer again, where it last was: for content that
   * moved under a still pointer (the grid scrolled itself).
   */
  refresh: () => void
}

function inOrder<K extends string | number>(a: K, b: K): [K, K] {
  return a < b ? [a, b] : [b, a]
}

export function useDragSelect<K extends string | number>(
  options: DragSelectOptions<K>
): DragSelect<K> {
  // Read by the window listeners, which must not be rebuilt per render.
  const optionsRef = useRef(options)
  useLayoutEffect(() => {
    optionsRef.current = options
  })
  const anchorRef = useRef<K | null>(null)
  const lastRef = useRef<K | null>(null)
  const pointRef = useRef<DragPoint | null>(null)
  // Where the press was, until the pointer leaves the slop around it.
  const pressRef = useRef<{ x: number; y: number } | null>(null)
  const draggedRef = useRef(false)
  const stopListeningRef = useRef<(() => void) | null>(null)
  const [range, setRangeState] = useState<DragRange<K> | null>(null)
  // What was last drawn, compared here rather than in a state updater, which
  // React may still render once to find is a no-op: a move within a day must
  // not render anything.
  const drawnRef = useRef<DragRange<K> | null>(null)
  const setRange = useCallback((next: DragRange<K> | null) => {
    const drawn = drawnRef.current
    if (
      drawn === next ||
      (drawn && next && drawn.first === next.first && drawn.last === next.last)
    ) {
      return
    }
    drawnRef.current = next
    setRangeState(next)
  }, [])

  // Forgets the drag and its listeners, leaving the drawn range alone.
  const release = useCallback(() => {
    anchorRef.current = null
    lastRef.current = null
    pointRef.current = null
    pressRef.current = null
    const stop = stopListeningRef.current
    stopListeningRef.current = null
    if (stop) {
      stop()
      optionsRef.current.onEnd?.()
    }
  }, [])

  const cancel = useCallback(() => {
    release()
    setRange(null)
  }, [release, setRange])

  // A new scope drops the drawn range here, while rendering (setting it in an
  // effect would repaint twice), and the listeners in the effect below.
  const { scope } = options
  const [seenScope, setSeenScope] = useState(scope)
  if (seenScope !== scope) {
    setSeenScope(scope)
    setRangeState(null)
  }
  useEffect(
    () => () => {
      release()
      drawnRef.current = null
    },
    [scope, release]
  )

  const follow = useCallback(
    (point: DragPoint) => {
      const anchor = anchorRef.current
      if (anchor === null) return false
      pointRef.current = point
      const press = pressRef.current
      if (press) {
        const moved = Math.hypot(point.clientX - press.x, point.clientY - press.y)
        if (moved < (optionsRef.current.slop ?? 0)) return false
        pressRef.current = null
      }
      const key = optionsRef.current.dayAt(point)
      if (key === null) return true
      lastRef.current = key
      if (key === anchor) {
        setRange(null)
        return true
      }
      if (!draggedRef.current) {
        draggedRef.current = true
        optionsRef.current.onDragged?.()
      }
      const [first, last] = inOrder(anchor, key)
      setRange({ first, last })
      return true
    },
    [setRange]
  )

  const refresh = useCallback(() => {
    if (pointRef.current && !pressRef.current) follow(pointRef.current)
  }, [follow])

  const start = (event: ReactPointerEvent<Element>, day: K) => {
    if (event.button !== 0 || event.pointerType === 'touch') return
    cancel()
    anchorRef.current = day
    lastRef.current = day
    draggedRef.current = false
    pressRef.current = optionsRef.current.slop ? { x: event.clientX, y: event.clientY } : null

    const onMove = (move: PointerEvent) => {
      // The button was let go where no release reached us.
      if ((move.buttons & 1) !== 1) {
        cancel()
        return
      }
      const point = { clientX: move.clientX, clientY: move.clientY, target: move.target }
      if (follow(point)) optionsRef.current.onMove?.(point)
    }
    const onUp = (up: PointerEvent) => {
      if (up.button !== 0) return
      const anchor = anchorRef.current
      const key = lastRef.current
      cancel()
      if (anchor === null || key === null || anchor === key) return
      const [first, last] = inOrder(anchor, key)
      // A pen keeps the pressed element as the pointer's target, so its release
      // also clicks it; swallow that one click so whatever the press was on
      // does not open over what this drag opens. (A mouse clicks the common
      // ancestor of the press and the release.)
      if (up.pointerType === 'pen') {
        const swallow = (click: MouseEvent) => click.stopPropagation()
        window.addEventListener('click', swallow, { capture: true, once: true })
        setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 100)
      }
      optionsRef.current.onSelect(first, last)
    }
    // Capture phase, and stopped: the Escape that cancels a drag must not also
    // close a sheet behind the grid.
    const onKey = (key: KeyboardEvent) => {
      if (key.key !== 'Escape') return
      key.stopPropagation()
      cancel()
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', cancel)
    window.addEventListener('blur', cancel)
    window.addEventListener('keydown', onKey, true)
    stopListeningRef.current = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', cancel)
      window.removeEventListener('blur', cancel)
      window.removeEventListener('keydown', onKey, true)
    }
  }

  return { range, start, refresh }
}
